import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import { Form, Input, Row, Col, Switch, Typography, Divider, App } from "antd";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import type { OrderFinancialVO, ChooseProduct } from "./orders";
import { getProductCompanyList, useMetadataStore, getProductInfo, calculatePremiumCapacity, type PremiumCapacityResponse, useConfigStore } from "@ifa/utils";
import type { ProductCompanyListVO, ProductInfoVO } from "@ifa/utils";
import { SelectInput } from "../../components/form/SelectInput";
import { AmountInput } from "../../components/form/AmountInput";
import { useFormConfig } from "../../hooks/useFormConfig";
import styles from "./FinancialForm.module.css";
import { isZuuFinancialStepVariant } from "../order-fna/fnaVariant.js";
import {
  getQuestionValueFromListByVariant,
  updateQuestionListByVariant,
} from "../order-fna/fnaQuestionAccess.js";
import { deriveZuuStep2Draft } from "../order-fna/zuuStep2Draft";
import {
  ZuuFnaForm,
  type ZuuFinancialAnalysisSectionProps,
  type ZuuOnlyProvideOneProductSectionProps,
  type ZuuProductSelectionSectionProps,
  type ZuuPremiumFinancingSectionProps,
  type ZuuSuggestionsSectionProps,
} from "../order-fna/ZuuFnaForm";
import { ZuuFinancialInfoStep2Section } from "../order-fna/ZuuFinancialInfoStep2Section";
import { shouldShowZuuRetirementFields, validateZuuFinancialInfoStep2, validateZuuPremiumFinancing } from "../order-fna/zuuValidation";
import { LegacyFnaForm } from "./LegacyFnaForm";
import type { OrderVO } from "../OrderForms/orders";
const { Title, Text } = Typography;
const cx = (...classNames: Array<string | false | null | undefined>) => classNames.filter(Boolean).join(" ");

interface FinancialFormProps {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  isFinancialInfoRequired?: boolean;
  readOnly?: boolean;
  detailDisplay?: boolean;
  onOpenSelector?: (context: { type: string; index: number }, productType?: string) => void;
  activeSection?: "financial-info" | "fna";
  orderData: OrderVO;
  zuuReferenceLatestFna?: {
    orderNo?: string;
    signTime?: string;
    onReference: () => void;
  } | null;
}

export interface FinancialFormRef {
  validate: () => Promise<any>;
}

export const FinancialForm = forwardRef<FinancialFormRef, FinancialFormProps>(
  ({ data = {}, onChange, isFinancialInfoRequired, readOnly = false, detailDisplay = false, onOpenSelector, activeSection = "financial-info", orderData, zuuReferenceLatestFna = null }, ref) => {
    const [form] = Form.useForm();
    const { orderId } = useParams();
    const { formRequiredConfig } = useMetadataStore;
    const { applyDefaultValues } = useFormConfig("FinancialForm", formRequiredConfig, data);
    const { message } = App.useApp();
    const productSectionRef = React.useRef<HTMLDivElement>(null);

    // Track product validation status
    const [productValidation, setProductValidation] = useState<Record<number, { paymentTermValid: boolean; benefitTermValid: boolean }>>({});

    // 保费承担能力数据（从 API 获取）
    const [premiumCapacity, setPremiumCapacity] = useState<PremiumCapacityResponse | null>(null);
    const brandId = useConfigStore((state) => state.brandId);
    const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
    const isZuuFinancialStepMode = isZuuFinancialStepVariant({
      brandId,
      tenantCode,
    });
    const shouldShowZuuReferenceLatestFna = false;

    // Define helper functions before useImperativeHandle
    const getQuestionValue = (section: "financialAnalysis" | "transferInsuranceDeclaration" | "suggestionsAndReasons", questionKey: string, type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO") => {
      const list = data[section] || [];
      return getQuestionValueFromListByVariant(list, questionKey, type, {
        isZuuMode: isZuuFinancialStepMode,
      });
    };

    useImperativeHandle(ref, () => ({
      validate: async () => {
        // 如果没有开启财务资料，不需要校验
        if (!isFinancialInfoRequired) {
          return Promise.resolve();
        }

        // ZUU 财务基本信息步骤也需要校验
        if (activeSection === "financial-info" && isZuuFinancialStepMode) {
          const errors = validateZuuFinancialInfoStep2(data, {
            isFinancialInfoRequired,
            showRetirementFields: shouldShowZuuRetirementFields(orderData?.policyholder),
          });
          if (errors.length > 0) {
            form.setFields(errors);
            form.scrollToField(errors[0].name);
            throw { errorFields: errors };
          }
          return Promise.resolve();
        }

        // 只在 FNA 步骤才进行校验
        if (activeSection === "fna") {
          const errors = [];

          // 校验表单字段
          try {
            await form.validateFields();
          } catch (error: any) {
            if (error.errorFields) {
              errors.push(...error.errorFields);
            }
          }

          // 检查问题 4.1 的输入框（当选择 G 时）
          if (getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO") === "G") {
            const singlePaymentLimit = getQuestionValue("financialAnalysis", "singlePremiumPaymentLimit", "INPUT");
            if (!singlePaymentLimit || singlePaymentLimit === 0) {
              errors.push({
                name: ["singlePremiumPaymentLimit"],
                errors: ["請輸入金額"],
              });
            }
          }

          if (!isZuuFinancialStepMode &&
            getQuestionValue("financialAnalysis", "continuedContributionReason", "RADIO") === "B"
          ) {
            const otherReason = getQuestionValue("financialAnalysis", "continuedContributionReasonOther", "INPUT");
            if (!otherReason || otherReason.trim() === "") {
              errors.push({
                name: ["continuedContributionReasonOther"],
                errors: ["請輸入原因"],
              });
            }
          }

          if (isZuuFinancialStepMode) {
            errors.push(...validateZuuPremiumFinancing(data));
          }

          // 需求 2.1：校验所有产品计划不能都为空
          const allProducts = data.chooseProducts || [];
          const hasAnyProduct = allProducts.some((p) => p.productCode);
          if (!hasAnyProduct) {
            errors.push({
              name: ["chooseProducts"],
              errors: ["介紹的產品計劃不能為空"],
            });
          }

          // 需求 3.1：校验至少要勾选一个产品
          const selectedProducts = allProducts.filter((p) => p.choose);
          if (hasAnyProduct && selectedProducts.length === 0) {
            errors.push({
              name: ["chooseProducts"],
              errors: ["請勾選一款介紹的產品投保"],
            });
          }

          // 需求 1.1：校验只能提供一家保险公司时必须填写原因
          const productsWithCompany = allProducts.filter((p) => p.companyCode);
          if (productsWithCompany.length >= 1) {
            // 获取去重后的保险公司数量
            const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
            const isSameCompany = uniqueCompanies.size === 1;

            if (isSameCompany && !data.onlyProvideOneProductReason?.trim()) {
              errors.push({
                name: ["onlyProvideOneProductReason"],
                errors: ["請填寫只能提供一間保險公司建議之原因"],
              });
            }
          }

          if (errors.length > 0) {
            // 滚动到第一个错误字段
            if (errors[0].name) {
              if (errors[0].name[0] === "chooseProducts") {
                // 如果是产品选择错误，滚动到产品选择区域
                if (productSectionRef.current) {
                  productSectionRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              } else {
                // 如果是表单字段错误，使用表单的滚动方法
                form.scrollToField(errors[0].name);
              }
            }
            throw { errorFields: errors };
          }
        }

        return Promise.resolve();
      },
    }));

    useEffect(() => {
      // 同步财务资料基本信息
      form.setFieldsValue(data);

      // 同步 FNA 数据到表单字段
      if (activeSection === "fna") {
        form.setFieldsValue({
          // 三、財務需要分析
          insurancePurchaseObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX"),
          additionalLifeCoverageAmountOption: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountOption", "RADIO"),
          additionalLifeCoverageAmountHKD: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountHKD", "INPUT"),
          additionalLifeCoverageAmountUSD: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountUSD", "INPUT"),
          additionalLifeCoverageAmount: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmount", "INPUT"),
          additionalCriticalIllnessCoverageAmount: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmount", "INPUT"),
          additionalCriticalIllnessCoverageAmountOption: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountOption", "RADIO"),
          additionalCriticalIllnessCoverageAmountHKD: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountHKD", "INPUT"),
          additionalCriticalIllnessCoverageAmountUSD: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountUSD", "INPUT"),
          insurancePurchaseMedicalObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseMedicalObjectives", "RADIO"),
          additionalTargetAmountOption: getQuestionValue("financialAnalysis", "additionalTargetAmountOption", "RADIO"),
          additionalTargetAmountHKD: getQuestionValue("financialAnalysis", "additionalTargetAmountHKD", "INPUT"),
          additionalTargetAmountUSD: getQuestionValue("financialAnalysis", "additionalTargetAmountUSD", "INPUT"),
          additionalTargetYears: getQuestionValue("financialAnalysis", "additionalTargetYears", "INPUT"),
          insurancePurchaseOtherObjectiveOptions: getQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectiveOptions", "RADIO"),
          additionalSavingsTargetReturnPeriod: getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", "INPUT"),
          additionalSavingsTargetReturnAmount: getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", "INPUT"),
          additionalInvestmentTargetReturnPeriod: getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnPeriod", "INPUT"),
          additionalInvestmentTargetReturnAmount: getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnAmount", "INPUT"),
          insurancePurchaseOtherObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectives", "INPUT"),
          investmentOptionManagementPreference: getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO"),
          investmentWithdrawalStartTime: getQuestionValue("financialAnalysis", "investmentWithdrawalStartTime", "RADIO"),
          investmentLongTermGoal: getQuestionValue("financialAnalysis", "investmentLongTermGoal", "RADIO"),
          investmentExpectationIfMarketPoor: getQuestionValue("financialAnalysis", "investmentExpectationIfMarketPoor", "RADIO"),
          policyTargetExpectedTime: getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO"),
          premiumToDisposableIncomeRatio: getQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", "RADIO"),
          premiumToLiquidAssetsRatio: getQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", "RADIO"),
          intendedPremiumPaymentMethods: getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX"),
          willingPremiumPaymentPeriodRange: getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO"),
          // 四、保費融資
          isPremiumFinanced: data.isPremiumFinanced,
          premiumFinancingRepaymentSource: data.premiumFinancingRepaymentSources?.[0],
          premiumFinancingRepaymentSourceOther: data.premiumFinancingRepaymentSourceOther,
          outstandingMortgageLoanAmount: data.outstandingMortgageLoanAmount,
          outstandingOtherLoanAmount: data.outstandingOtherLoanAmount,
          premiumFinancingEstimateConsent: data.premiumFinancingEstimateConsent === true ? "A" : data.premiumFinancingEstimateConsent === false ? "B" : undefined,
          premiumFinancingLenderName: data.premiumFinancing?.[0]?.lenderName,
          premiumFinancingLoanAmount: data.premiumFinancing?.[0]?.loanAmount,
          premiumFinancingLoanInterestRate: data.premiumFinancing?.[0]?.loanInterestRate,
          premiumFinancingLoanTerm: data.premiumFinancing?.[0]?.loanTerm,
          premiumFinancingRepaymentAmount: data.premiumFinancing?.[0]?.repaymentAmount,
          premiumFinancingImportantDisclosureConfirmed: data.premiumFinancingImportantDisclosureConfirmed,
          premiumFinancingRepaymentConfirmed: data.premiumFinancingRepaymentConfirmed,
          ...(!isZuuFinancialStepMode
            ? {
                hasExistingPolicyAsMortgage: data.hasExistingPolicyAsMortgage,
                usingExistingPolicyFundsForNewPolicy: getQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", "RADIO"),
                intermediaryAssessmentAndComments: getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndComments", "RADIO"),
                intermediaryAssessmentAndCommentsOther: getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndCommentsOther", "INPUT"),
              }
            : {}),
          // 七、建議及原因
          suitabilityDeviationOccurred: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO"),
          suitabilityDeviationFollowUpYesOptions: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpYesOptions", "RADIO"),
          suitabilityDeviationFollowUpNoOptions: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpNoOptions", "RADIO"),
          reasonForRecommendingDespiteDeviation: getQuestionValue("suggestionsAndReasons", "reasonForRecommendingDespiteDeviation", "RADIO"),
          otherReasonForRecommendation: getQuestionValue("suggestionsAndReasons", "otherReasonForRecommendation", "INPUT"),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, form, activeSection]);

    // 调用保费承担能力计算 API
    useEffect(() => {
      const fetchPremiumCapacity = async () => {
        if (isZuuFinancialStepMode) {
          setPremiumCapacity(null);
          return;
        }

        if (!orderId || !data.financialAnalysis || data.financialAnalysis.length === 0) {
          return;
        }

        try {
          const response = await calculatePremiumCapacity({
            orderId: Number(orderId),
            financialAnalysis: data.financialAnalysis,
          });
          setPremiumCapacity(response);
        } catch (error) {
          console.error("获取保费承担能力失败:", error);
          // API 错误会由全局错误处理器显示，这里只需要记录日志
        }
      };

      fetchPremiumCapacity();
    }, [orderId, data.financialAnalysis, isZuuFinancialStepMode]);

    const [companyList, setCompanyList] = useState<ProductCompanyListVO[]>([]);
    const { selectMetadataList } = useMetadataStore();

    const getOptions = (code: string, fallback: any[] = []) => {
      const meta = selectMetadataList.find((m) => m.code === code);
      if (meta?.items?.length) {
        return meta.items.map((i) => ({
          label: i.name || i.key,
          value: i.key,
        }));
      }
      return fallback;
    };

    useEffect(() => {
      if (orderId) {
        getProductCompanyList().then((res) => {
          setCompanyList(res);
        });
        // getProductList({ pageSize: 1000 }).then((res) => {
        //   setProductList(res.content || []);
        // });
      }
    }, [orderId]);

    // Validate products against payment term and benefit period requirements
    useEffect(() => {
      const validateProducts = async () => {
        const products = data.chooseProducts || [];
        const validation: Record<number, { paymentTermValid: boolean; benefitTermValid: boolean }> = {};

        // Get derived requirements from FNA
        const q4_1 = getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO");
        const q2 = getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO");

        for (let i = 0; i < products.length; i++) {
          const product = products[i];
          if (!product.productCode) {
            validation[i] = { paymentTermValid: true, benefitTermValid: true };
            continue;
          }

          try {
            const productInfo: ProductInfoVO = await getProductInfo(product.productCode);

            // Validate payment term (供款期数)
            let paymentTermValid = true;
            if (q4_1 && productInfo.paymentTerm) {
              const paymentTermMap: Record<string, string[]> = {
                A: ["2", "3", "4", "5"],
                B: ["6", "7", "8", "9", "10"],
                C: ["11", "12", "13", "14", "15"],
                D: ["16", "17", "18", "19", "20"],
                E: ["21", "22", "23", "24", "25", "30", "35", "40", "45", "50"],
                F: ["whole_life", "to_age_100"],
                G: ["1"], // 一次性付款
              };

              const expectedTerms = paymentTermMap[q4_1];
              if (expectedTerms) {
                paymentTermValid = productInfo.paymentTerm.some((term) => expectedTerms.includes(term));
              }
            }

            // Validate benefit period (保障年限)
            let benefitTermValid = true;
            if (q2 && productInfo.policyPeriods) {
              if (q2 === "G") {
                benefitTermValid = productInfo.policyPeriods.includes("whole_life");
              } else {
                // For other options, accept to_age_100 or numeric periods
                benefitTermValid = productInfo.policyPeriods.some((period) => period === "to_age_100" || !isNaN(Number(period)));
              }
            }

            validation[i] = { paymentTermValid, benefitTermValid };
          } catch (error) {
            console.error(`Failed to validate product ${product.productCode}:`, error);
            validation[i] = { paymentTermValid: true, benefitTermValid: true };
          }
        }

        setProductValidation(validation);
      };

      if (data.chooseProducts && data.chooseProducts.length > 0) {
        validateProducts();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.chooseProducts, data.financialAnalysis]);

    // Auto-update onlyProvideOneProduct based on all products' company codes
    useEffect(() => {
      // 需求 1）：判断所有填写了产品计划的卡片是否来自同一家保险公司
      const allProducts = data.chooseProducts || [];
      const productsWithCompany = allProducts.filter((p) => p.companyCode);

      // 获取去重后的保险公司数量
      const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
      const isSameCompany = productsWithCompany.length > 0 && uniqueCompanies.size === 1;

      // 只有当计算结果与当前值不同时才更新，避免无限循环
      if (data.onlyProvideOneProduct !== isSameCompany) {
        onChange({ ...data, onlyProvideOneProduct: isSameCompany });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.chooseProducts, data.onlyProvideOneProduct]);

    // Initialize chooseProducts with 1 empty product if empty
    useEffect(() => {
      // 只在 FNA 页面且产品列表为空时初始化
      if (activeSection === "fna" && (!data.chooseProducts || data.chooseProducts.length === 0)) {
        const emptyProduct: ChooseProduct = {
          productCode: null,
          choose: false,
        };
        onChange({
          ...data,
          chooseProducts: [{ ...emptyProduct }],
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    // Initialize numeric fields with 0 when financial info is required
    useEffect(() => {
      if (!isZuuFinancialStepMode && isFinancialInfoRequired && activeSection === "financial-info") {
        // Helper to merge defaults with existing values, converting null to 0 for numbers
        const mergeWithDefaults = (current: any, defaults: any) => {
          if (!current) return defaults;
          const merged = { ...current, ...defaults };
          Object.keys(defaults).forEach((key) => {
            if (current[key] !== undefined && current[key] !== null) {
              merged[key] = current[key];
            }
          });
          return merged;
        };

        let initializedData = {
          ...data,
          income: mergeWithDefaults(data.income, {
            salaryCommissionBonus: 0,
            rentalIncome: 0,
            hasOtherIncome: false,
            otherIncomeName: "",
            otherIncomeAmount: 0,
          }),
          expense: mergeWithDefaults(data.expense, {
            personalAndFamilyExpense: 0,
            mortgageLoanRepayment: 0,
            insuranceContribution: 0,
            hasOtherExpense: false,
            otherExpenseName: "",
            otherExpenseAmount: 0,
          }),
          currentAssets: mergeWithDefaults(data.currentAssets, {
            cashAndDeposits: 0,
            liquidInvestments: 0,
            hasOtherCurrentAssets: false,
            otherCurrentAssetsName: "",
            otherCurrentAssetsAmount: 0,
          }),
          nonCurrentAssets: mergeWithDefaults(data.nonCurrentAssets, {
            property: 0,
            otherNonCurrentAssets: 0,
          }),
          liabilities: mergeWithDefaults(data.liabilities, {
            mortgageLoan: 0,
            personalCreditCardLoan: 0,
            hasOtherLiabilities: false,
            otherLiabilityName: "",
            otherLiabilityAmount: 0,
          }),
          protection: mergeWithDefaults(data.protection, {
            lifeInsurance: 0,
            criticalIllnessInsurance: 0,
            medicalInsuranceType: undefined,
            medicalInsuranceAmount: 0,
            accidentInsurance: 0,
            otherExistingProtection: 0,
            attachmentRemarks: "",
          }),
        };

        // 应用 formRequiredConfig 中的默认值
        const dataWithConfigDefaults = applyDefaultValues(initializedData);
        if (dataWithConfigDefaults !== initializedData) {
          initializedData = dataWithConfigDefaults;
        }

        // Only update if there are actual changes
        const hasChanges =
          JSON.stringify(initializedData.income) !== JSON.stringify(data.income) ||
          JSON.stringify(initializedData.expense) !== JSON.stringify(data.expense) ||
          JSON.stringify(initializedData.currentAssets) !== JSON.stringify(data.currentAssets) ||
          JSON.stringify(initializedData.nonCurrentAssets) !== JSON.stringify(data.nonCurrentAssets) ||
          JSON.stringify(initializedData.liabilities) !== JSON.stringify(data.liabilities) ||
          JSON.stringify(initializedData.protection) !== JSON.stringify(data.protection);

        if (hasChanges) {
          onChange(initializedData);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFinancialInfoRequired, activeSection, data, onChange, isZuuFinancialStepMode]);

    // ZUU 没有 4.2，确保遗留值被清空
    useEffect(() => {
      if (activeSection !== "fna" || !isZuuFinancialStepMode) return;

      const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
      const nextFinancialAnalysis = currentFinancialAnalysis.filter((q) => q.question !== "continuedContributionReason" && q.question !== "continuedContributionReasonOther");

      if (nextFinancialAnalysis.length !== currentFinancialAnalysis.length) {
        form.setFieldValue("continuedContributionReason", undefined);
        form.setFieldValue("continuedContributionReasonOther", undefined);
        onChange({ ...data, financialAnalysis: nextFinancialAnalysis });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection, isZuuFinancialStepMode]);

    // 需求1：当问题1的选项E取消勾选时，清空投资相关字段
    useEffect(() => {
      if (activeSection !== "fna") return;

      const objectives = getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX");

      // 如果选项E未被勾选，清空所有投资相关字段
      if (!objectives || !objectives.includes("E")) {
        const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
        const keysToCheck = ["investmentOptionManagementPreference", "investmentWithdrawalStartTime", "investmentLongTermGoal", "investmentExpectationIfMarketPoor"];

        let hasValue = false;
        keysToCheck.forEach((key) => {
          const q = currentFinancialAnalysis.find((q) => q.question === key);
          if (q) {
            const hasNonEmpty = q.items?.some((i) => i.value !== false && i.value !== "" && i.value !== 0 && i.value !== undefined && i.value !== null);
            if (hasNonEmpty) hasValue = true;
          }
        });

        if (hasValue) {
          const newList = currentFinancialAnalysis.map((q) => {
            if (keysToCheck.includes(q.question || "")) {
              // 清空该问题的所有值
              if (q.type === "RADIO" || q.type === "CHECKBOX") {
                return {
                  ...q,
                  items: (q.items || []).map((i) => ({ ...i, value: false })),
                };
              } else if (q.type === "INPUT") {
                return {
                  ...q,
                  items: [{ key: "A", value: "" }],
                };
              }
            }
            return q;
          });

          // 清空表单验证状态
          keysToCheck.forEach((key) => {
            form.setFieldValue(key, undefined);
          });

          onChange({ ...data, financialAnalysis: newList });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection]);

    // 需求2：当 investmentOptionManagementPreference 从 A 切换到 B 或清空时，清空子问题
    useEffect(() => {
      if (activeSection !== "fna") return;

      const managementPreference = getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO");

      // 如果值不是 "A"，清空子问题
      if (managementPreference !== "A") {
        const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
        const keysToCheck = ["investmentWithdrawalStartTime", "investmentLongTermGoal", "investmentExpectationIfMarketPoor"];

        let hasValue = false;
        keysToCheck.forEach((key) => {
          const q = currentFinancialAnalysis.find((q) => q.question === key);
          if (q) {
            const hasNonEmpty = q.items?.some((i) => i.value !== false && i.value !== "" && i.value !== 0 && i.value !== undefined && i.value !== null);
            if (hasNonEmpty) hasValue = true;
          }
        });

        if (hasValue) {
          const newList = currentFinancialAnalysis.map((q) => {
            if (keysToCheck.includes(q.question || "")) {
              // 清空该问题的所有值
              if (q.type === "RADIO" || q.type === "CHECKBOX") {
                return {
                  ...q,
                  items: (q.items || []).map((i) => ({ ...i, value: false })),
                };
              } else if (q.type === "INPUT") {
                return {
                  ...q,
                  items: [{ key: "A", value: "" }],
                };
              }
            }
            return q;
          });

          // 清空表单验证状态
          keysToCheck.forEach((key) => {
            form.setFieldValue(key, undefined);
          });

          onChange({ ...data, financialAnalysis: newList });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection]);

    const handleChange = (field: keyof OrderFinancialVO | string, value: any) => {
      onChange({ ...data, [field]: value });
    };

    const handleNestedChange = (section: keyof OrderFinancialVO, field: string, value: any) => {
      const currentSection = (data[section] as any) || {};
      const newData = {
        ...data,
        [section]: { ...currentSection, [field]: value },
      };
      onChange(newData);
      // Form values are synced via useEffect, but for immediate feedback we might want to set them?
      // Actually the useEffect [data] will handle it, but it might be slightly delayed.
      // Better to set form value directly too to avoid lag if controlled.
      form.setFieldValue([section, field], value);
    };

    // Premium Financing Helpers
    const handleAddPremiumFinancing = () => {
      const currentList = data.premiumFinancing || [];
      onChange({
        ...data,
        premiumFinancing: [...currentList, {}],
      });
    };

    const handleRemovePremiumFinancing = (index: number) => {
      const currentList = data.premiumFinancing || [];
      const newList = currentList.filter((_, i) => i !== index);
      onChange({
        ...data,
        premiumFinancing: newList,
      });
    };

    const handlePremiumFinancingChange = (index: number, field: string, value: any) => {
      const currentList = data.premiumFinancing || [];
      const newList = [...currentList];
      newList[index] = { ...newList[index], [field]: value };
      onChange({
        ...data,
        premiumFinancing: newList,
      });
    };

    // New helpers for Question-based structure (matching fna.json)
    const updateQuestionValue = (
      section: "financialAnalysis" | "transferInsuranceDeclaration" | "suggestionsAndReasons",
      questionKey: string,
      value: any,
      type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO",
    ) => {
      const list = (data[section] || []).filter((q) => q.question);
      const newList = updateQuestionListByVariant(list, questionKey, value, type, {
        isZuuMode: isZuuFinancialStepMode,
      });

      if (section === "financialAnalysis" && isZuuFinancialStepMode && ["additionalLifeCoverageAmountOption", "additionalCriticalIllnessCoverageAmountOption", "additionalTargetAmountOption"].includes(questionKey)) {
        console.log("[ZUU][updateQuestionValue]", {
          questionKey,
          value,
          type,
          questionAfterUpdate: newList[newList.length - 1],
        });
      }

      onChange({ ...data, [section]: newList });
    };

    // --- Subtotal Calculations ---
    const parsePositiveInteger = (value: unknown): number | undefined => {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return undefined;
      }
      const normalized = Math.trunc(value);
      return normalized >= 0 ? normalized : undefined;
    };

    // A) Income
    const totalIncomeDerived =
      (data.income?.salaryCommissionBonus || 0) + (data.income?.rentalIncome || 0) + (data.income?.hasOtherIncome && data.income?.otherIncomeAmount ? data.income.otherIncomeAmount : 0);
    const totalIncome = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).monthlyIncomesTotal) ?? totalIncomeDerived) : totalIncomeDerived;

    // B) Expense
    const totalExpenseDerived =
      (data.expense?.personalAndFamilyExpense || 0) +
      (data.expense?.mortgageLoanRepayment || 0) +
      (data.expense?.insuranceContribution || 0) +
      (data.expense?.hasOtherExpense && data.expense?.otherExpenseAmount ? data.expense.otherExpenseAmount : 0);
    const totalExpense = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).monthlyExpensesTotal) ?? totalExpenseDerived) : totalExpenseDerived;

    // C) Current Assets
    const totalCurrentAssetsDerived =
      (data.currentAssets?.cashAndDeposits || 0) +
      (data.currentAssets?.liquidInvestments || 0) +
      (data.currentAssets?.hasOtherCurrentAssets && data.currentAssets?.otherCurrentAssetsAmount ? data.currentAssets.otherCurrentAssetsAmount : 0);
    const totalCurrentAssets = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).liquidAssetsTotal) ?? totalCurrentAssetsDerived) : totalCurrentAssetsDerived;

    // D) Non-Current Assets
    const totalNonCurrentAssets = (data.nonCurrentAssets?.property || 0) + (data.nonCurrentAssets?.otherNonCurrentAssets || 0);

    // E) Liabilities
    const totalLiabilities =
      (data.liabilities?.mortgageLoan || 0) +
      (data.liabilities?.personalCreditCardLoan || 0) +
      (data.liabilities?.hasOtherLiabilities && data.liabilities?.otherLiabilityAmount ? data.liabilities.otherLiabilityAmount : 0);

    // Main Summaries
    const surplusDeficit = totalIncome - totalExpense;
    const netAssets = totalCurrentAssets + totalNonCurrentAssets - totalLiabilities;

    // Header Box Style
    const headerBoxStyle: React.CSSProperties = {
      backgroundColor: isZuuFinancialStepMode ? "#E9EEF5" : "var(--theme-tertiary)",
      border: isZuuFinancialStepMode ? "2px solid #3E679A" : "1px solid var(--theme-primary)",
      borderRadius: isZuuFinancialStepMode ? 8 : 4,
      padding: isZuuFinancialStepMode ? "12px 16px" : "10px 16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 24,
      color: isZuuFinancialStepMode ? "#274C77" : "var(--theme-primary)",
      fontSize: isZuuFinancialStepMode ? 15 : 16,
      fontWeight: 600,
    };

    // Subsection Header Style
    const subSectionHeaderStyle: React.CSSProperties = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: isZuuFinancialStepMode ? 12 : 16,
      color: isZuuFinancialStepMode ? "#475569" : "var(--theme-primary)",
      fontSize: 14,
    };

    // Calculate values for section 3 display
    const getRatioFromKey = (key: string | undefined): number => {
      switch (key) {
        case "A":
          return 0.1;
        case "B":
          return 0.2;
        case "C":
          return 0.3;
        case "D":
          return 0.4;
        case "E":
          return 0.5;
        case "F":
          return 1.0;
        default:
          return 0;
      }
    };

    const ratioA = getRatioFromKey(getQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", "RADIO"));
    const ratioB = getRatioFromKey(getQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", "RADIO"));

    // 使用 API 返回的数据，如果没有则使用本地计算作为后备
    const a_monthly = premiumCapacity?.monthlyDisposableIncome ?? surplusDeficit;
    const a_yearly = premiumCapacity?.annualDisposableIncome ?? surplusDeficit * 12;
    const a1_monthly = premiumCapacity?.maxMonthlyPremium ?? a_monthly * ratioA;
    const a1_yearly = premiumCapacity?.maxAnnualPremium ?? a_yearly * ratioA;

    const b_assets = premiumCapacity?.liquidAssets ?? totalCurrentAssets;
    const b1_amount = premiumCapacity?.maxLiquidAssetPremium ?? b_assets * ratioB;
    const annualizePremium = (premiumPerPeriod?: number, paymentFrequency?: string) => {
      if (premiumPerPeriod === undefined || premiumPerPeriod === null || !Number.isFinite(Number(premiumPerPeriod))) {
        return 0;
      }

      const multiplierMap: Record<string, number> = {
        SINGLE_PAYMENT: 1,
        ANNUALLY: 1,
        SEMI_ANNUALLY: 2,
        QUARTERLY: 4,
        MONTHLY: 12,
      };

      return Number(premiumPerPeriod) * (multiplierMap[paymentFrequency || ""] || 1);
    };

    const totalRecommendedAnnualPremium = (orderData?.productPlans || []).reduce((sum, plan) => {
      const basePremium = annualizePremium(plan?.premiumPerPeriod, plan?.paymentFrequency);
      const riderPremium = (plan?.riders || []).reduce((riderSum: number, rider: any) => riderSum + annualizePremium(rider?.premiumPerPeriod, rider?.paymentFrequency), 0);
      return sum + basePremium + riderPremium;
    }, 0);

    const zuuStep2Draft = deriveZuuStep2Draft({
      financial: data,
      activeSection,
      isFinancialInfoRequired,
    });

    const updateZuuDraftAmount = (
      group: "availableIncome" | "liquidAssets",
      field: "monthlyIncomeLast24Months" | "monthlyExpenseLast24Months" | "currentLiquidAssets" | "monthlyDisposableIncomeAfterRetirement" | "additionalLiquidAssetsAfterRetirement",
      value: number | undefined,
    ) => {
      const normalizedValue = parsePositiveInteger(value);
      const nextData: OrderFinancialVO = {
        ...data,
        monthlyIncomesTotal: undefined,
        monthlyExpensesTotal: undefined,
        liquidAssetsTotal: undefined,
        income: {
          ...(data.income || {}),
        },
        expense: {
          ...(data.expense || {}),
        },
        currentAssets: {
          ...(data.currentAssets || {}),
        },
      };

      if (field === "monthlyIncomeLast24Months") {
        nextData.income = {
          ...(nextData.income || {}),
          avgMonthlyIncome: normalizedValue ?? null,
        };
      } else if (field === "monthlyExpenseLast24Months") {
        nextData.expense = {
          ...(nextData.expense || {}),
          avgMonthlyExpense: normalizedValue ?? null,
        };
      } else if (field === "currentLiquidAssets") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          currentLiquidAssets: normalizedValue ?? null,
        };
      } else if (field === "monthlyDisposableIncomeAfterRetirement") {
        nextData.income = {
          ...(nextData.income || {}),
          expectedRetireIncome: normalizedValue ?? null,
        };
      } else if (field === "additionalLiquidAssetsAfterRetirement") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          expectedRetireLiquidAssets: normalizedValue ?? null,
        };
      }

      onChange(nextData);
    };

    const updateZuuDraftFreeAmount = (
      group: "availableIncome" | "liquidAssets",
      field: "monthlyDisposableIncomeAfterRetirement" | "additionalLiquidAssetsAfterRetirement",
      value: number | undefined,
    ) => {
      const normalizedValue = parsePositiveInteger(value);
      const nextData: OrderFinancialVO = {
        ...data,
        income: {
          ...(data.income || {}),
        },
        currentAssets: {
          ...(data.currentAssets || {}),
        },
      };

      if (field === "monthlyDisposableIncomeAfterRetirement") {
        nextData.income = {
          ...(nextData.income || {}),
          expectedRetireIncome: normalizedValue ?? null,
        };
      } else if (field === "additionalLiquidAssetsAfterRetirement") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          expectedRetireLiquidAssets: normalizedValue ?? null,
        };
      }

      onChange(nextData);
    };

    const formatCurrency = (val: number) => {
      return val.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const formatInteger = (val: number) => {
      return val.toLocaleString("en-US", { maximumFractionDigits: 0 });
    };
    const monthlyCurrencySuffix = isZuuFinancialStepMode ? "HKD/月" : undefined;
    const currencySuffix = isZuuFinancialStepMode ? "HKD" : undefined;
    const currencySuffixVariant = isZuuFinancialStepMode ? "box" : "plain";
    const showZuuRetirementFields = shouldShowZuuRetirementFields(orderData?.policyholder);
    const intendedPremiumPaymentMethods = (getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []) as string[];
    const premiumCapacityWarnings: string[] = [];
    if (isZuuFinancialStepMode && totalRecommendedAnnualPremium > 0) {
      if (intendedPremiumPaymentMethods.includes("A") && a1_yearly > 0 && totalRecommendedAnnualPremium > a1_yearly) {
        premiumCapacityWarnings.push(`按每月可動用收入推算，現有產品計劃的預估年繳保費 ${formatCurrency(totalRecommendedAnnualPremium)} HKD 高於可負擔上限 ${formatCurrency(a1_yearly)} HKD。`);
      }
      if (intendedPremiumPaymentMethods.includes("B") && b1_amount > 0 && totalRecommendedAnnualPremium > b1_amount) {
        premiumCapacityWarnings.push(`按流動資產推算，現有產品計劃的預估年繳保費 ${formatCurrency(totalRecommendedAnnualPremium)} HKD 高於可負擔上限 ${formatCurrency(b1_amount)} HKD。`);
      }
    }
    const zuuFinancialAnalysisProps: ZuuFinancialAnalysisSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          getQuestionValue,
          updateQuestionValue,
          shouldShowQuestion42: () => false,
          metrics: {
            aMonthly: a_monthly,
            aYearly: a_yearly,
            a1Monthly: a1_monthly,
            a1Yearly: a1_yearly,
            bAssets: b_assets,
            b1Amount: b1_amount,
          },
        }
      : undefined;
    const zuuPremiumFinancingProps: ZuuPremiumFinancingSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          readOnly,
          companyOptions: companyList.map((company) => ({
            label: company.name || company.code,
            value: company.code,
          })),
          onFieldChange: handleChange,
          onAddPremiumFinancing: handleAddPremiumFinancing,
          onRemovePremiumFinancing: handleRemovePremiumFinancing,
          onPremiumFinancingChange: handlePremiumFinancingChange,
        }
      : undefined;
    const zuuProductSelectionProps: ZuuProductSelectionSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          readOnly,
          productSectionRef,
          productValidation,
          getQuestionValue,
          onOpenSelector,
          onMessageError: message.error,
        }
      : undefined;
    const zuuOnlyProvideOneProductProps: ZuuOnlyProvideOneProductSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
        }
      : undefined;
    const zuuSuggestionsProps: ZuuSuggestionsSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          getQuestionValue,
          updateQuestionValue,
        }
      : undefined;

    return (
      <div className="financial-form" style={{ borderRadius: 6 }}>
        {/* 只有开关打开时才显示下面两个部分 */}
        {isFinancialInfoRequired && (
          <>
            {activeSection === "financial-info" && isZuuFinancialStepMode && (
              <Form form={form} layout="vertical" disabled={readOnly} className={cx(styles.financialForm, isZuuFinancialStepMode && styles.zuuFinancialStepForm)}>
                <ZuuFinancialInfoStep2Section
                  data={data}
                  onChange={onChange}
                  activeSection={activeSection}
                  isFinancialInfoRequired={isFinancialInfoRequired}
                  readOnly={readOnly}
                  showRetirementFields={showZuuRetirementFields}
                  detailDisplay={detailDisplay}
                />
              </Form>
            )}
            {activeSection === "financial-info" && !isZuuFinancialStepMode && (
              <Form form={form} layout="vertical" disabled={readOnly} className={cx(styles.financialForm, isZuuFinancialStepMode && styles.zuuFinancialStepForm)}>
                <>
                  {/* 1. 估計每月盈餘/超支 */}
                  <CustomCard title="一、財務資料" className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div style={headerBoxStyle}>
                      <span>{isZuuFinancialStepMode ? "1. 可動用收入" : "1. 估計每月盈餘/超支 (HK$/港幣)"}</span>
                      <span
                        style={
                          isZuuFinancialStepMode
                            ? {
                                fontSize: 11,
                                color: "#5b6f87",
                                fontWeight: 500,
                              }
                            : undefined
                        }
                      >
                        {isZuuFinancialStepMode ? "＊每月可動用收入將等同每月收入減去每月開支" : `A-B = ${surplusDeficit.toLocaleString()} HKD`}
                      </span>
                    </div>

                    {/* Income (A) */}
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "1a. 在過去二十四個月裡，您從所有收入來源所得的每月平均收入為？" : "A) 過去2年的平均每月收入"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalIncome.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>包括薪金、花紅、佣金、其他薪酬福利、物業租賃收入、銀行存款利息、債券利息及股息等。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyIncomeLast24Months.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("availableIncome", "monthlyIncomeLast24Months", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="薪金/佣金/花紅" name={["income", "salaryCommissionBonus"]}>
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.income?.salaryCommissionBonus ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("income", "salaryCommissionBonus", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="租金收入">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.income?.rentalIncome ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("income", "rentalIncome", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他收入 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.income?.hasOtherIncome || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      income: {
                                        ...data.income,
                                        hasOtherIncome: false,
                                        otherIncomeName: undefined,
                                        otherIncomeAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("income", newData.income);
                                  } else {
                                    handleNestedChange("income", "hasOtherIncome", val);
                                  }
                                }}
                              />
                            </div>
                            {data.income?.hasOtherIncome && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input placeholder="請輸入" value={data.income?.otherIncomeName} onChange={(e) => handleNestedChange("income", "otherIncomeName", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={monthlyCurrencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.income?.otherIncomeAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("income", "otherIncomeAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {/* Expense (B) */}
                      <Divider style={{ margin: "24px 0" }} />
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "1b. 在過去二十四個月裡，您每月平均開支為？" : "B) 過去2年的平均每月支出"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalExpense.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>包括樓宇按揭、租金、衣服、交通、借貸、保險費用及保費融資產生的利息等。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyExpenseLast24Months.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("availableIncome", "monthlyExpenseLast24Months", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="個人及家庭開支">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.personalAndFamilyExpense ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "personalAndFamilyExpense", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="按揭/借貸還款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.mortgageLoanRepayment ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "mortgageLoanRepayment", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="保險供款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.insuranceContribution ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "insuranceContribution", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他支出 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.expense?.hasOtherExpense || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      expense: {
                                        ...data.expense,
                                        hasOtherExpense: false,
                                        otherExpenseName: undefined,
                                        otherExpenseAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("expense", newData.expense);
                                  } else {
                                    handleNestedChange("expense", "hasOtherExpense", val);
                                  }
                                }}
                              />
                            </div>
                            {data.expense?.hasOtherExpense && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input placeholder="請輸入" value={data.expense?.otherExpenseName} onChange={(e) => handleNestedChange("expense", "otherExpenseName", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={monthlyCurrencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.expense?.otherExpenseAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("expense", "otherExpenseAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {isZuuFinancialStepMode && !isRetiredInStep1 && (
                        <div style={{ marginTop: 20 }}>
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              1c. 於到達退休年齡後，您預期的平均每月可動用收入為？
                            </Title>
                          </div>
                          <div className={styles.zuuQuestionNote}>
                            一般而言，退休後的可動用收入可經由不同方式獲得，例如：銀行存款及債券所產生之利息、股票股息、租金收入、從生意營運所得的收入，以及／或從家庭成員或信託所得的收入等。
                          </div>
                          <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
                          <div className={styles.zuuPendingField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              placeholder="請輸入"
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyDisposableIncomeAfterRetirement.amount ?? undefined}
                              onChange={(val) => updateZuuDraftFreeAmount("availableIncome", "monthlyDisposableIncomeAfterRetirement", val)}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 2. 資產及負債 */}
                    <div style={headerBoxStyle}>
                      <span>{isZuuFinancialStepMode ? "2. 流動資產" : "2. 資產及負債 (HK$/港幣)"}</span>
                      <span
                        style={
                          isZuuFinancialStepMode
                            ? {
                                fontSize: 11,
                                color: "#5b6f87",
                                fontWeight: 500,
                              }
                            : undefined
                        }
                      >
                        {isZuuFinancialStepMode ? "＊流動資產需按投資可隨時變現部分及退休後額外流動資產估算" : `C+D-E = ${netAssets.toLocaleString()} HKD`}
                      </span>
                    </div>

                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      {/* Current Assets (C) */}
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "2a. 您現時累積的流動資產約有多少？" : "C) 流動資產"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalCurrentAssets.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>流動資產是指可以容易變現為現金的資產。物業、藝術收藏品及藝術品均不能被視為流動資產。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={currencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.liquidAssets.currentLiquidAssets.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("liquidAssets", "currentLiquidAssets", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="現金及存款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.currentAssets?.cashAndDeposits ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("currentAssets", "cashAndDeposits", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="變現能力高的投資 (股票/債券等)">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.currentAssets?.liquidInvestments ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("currentAssets", "liquidInvestments", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他流動資產 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.currentAssets?.hasOtherCurrentAssets || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      currentAssets: {
                                        ...data.currentAssets,
                                        hasOtherCurrentAssets: false,
                                        otherCurrentAssetsName: undefined,
                                        otherCurrentAssetsAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("currentAssets", newData.currentAssets);
                                  } else {
                                    handleNestedChange("currentAssets", "hasOtherCurrentAssets", val);
                                  }
                                }}
                              />
                            </div>
                            {data.currentAssets?.hasOtherCurrentAssets && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input
                                      placeholder="請輸入"
                                      value={data.currentAssets?.otherCurrentAssetsName}
                                      onChange={(e) => handleNestedChange("currentAssets", "otherCurrentAssetsName", e.target.value)}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={currencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.currentAssets?.otherCurrentAssetsAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("currentAssets", "otherCurrentAssetsAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {isZuuFinancialStepMode && !isRetiredInStep1 && (
                        <div style={{ marginTop: 20 }}>
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              2b. 除上述2a的金額，您預期到退休年齡後額外獲得的流動資產約有多少？
                            </Title>
                          </div>
                          <div className={styles.zuuQuestionNote}>一般而言，退休後的流動資產可經由不同方式獲得，例如：強積金或退休儲蓄、銀行存款、股票或債券、遺產，以及從保單的保證紅利／現金等。</div>
                          <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
                          <div className={styles.zuuPendingField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={currencySuffix}
                              suffixVariant={currencySuffixVariant}
                              placeholder="請輸入"
                              value={zuuStep2Draft.financialInfo.liquidAssets.additionalLiquidAssetsAfterRetirement.amount ?? undefined}
                              onChange={(val) => updateZuuDraftFreeAmount("liquidAssets", "additionalLiquidAssetsAfterRetirement", val)}
                            />
                          </div>
                        </div>
                      )}

                      {!isZuuFinancialStepMode && (
                        <>
                          {/* Non-Current Assets (D) */}
                          <Divider style={{ margin: "24px 0" }} />
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              D) 非流動資產
                            </Title>
                            <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalNonCurrentAssets.toLocaleString()} HKD</span>
                          </div>
                          <Row gutter={24}>
                            <Col span={12}>
                              <Form.Item label="物業">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.nonCurrentAssets?.property ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("nonCurrentAssets", "property", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="其他 (如公司股份)">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.nonCurrentAssets?.otherNonCurrentAssets ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("nonCurrentAssets", "otherNonCurrentAssets", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          {/* Liabilities (E) */}
                          <Divider style={{ margin: "24px 0" }} />
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              E) 負債
                            </Title>
                            <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalLiabilities.toLocaleString()} HKD</span>
                          </div>
                          <Row gutter={24}>
                            <Col span={12}>
                              <Form.Item label="按揭貸款">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.liabilities?.mortgageLoan ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("liabilities", "mortgageLoan", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="私人/信用卡貸款">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.liabilities?.personalCreditCardLoan ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("liabilities", "personalCreditCardLoan", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                  marginBottom: 8,
                                }}
                              >
                                <Text>是否有其他負債 ?</Text>
                                <Switch
                                  checkedChildren="是"
                                  unCheckedChildren="否"
                                  checked={data.liabilities?.hasOtherLiabilities || false}
                                  onChange={(val) => {
                                    if (!val) {
                                      const newData = {
                                        ...data,
                                        liabilities: {
                                          ...data.liabilities,
                                          hasOtherLiabilities: false,
                                          otherLiabilityName: undefined,
                                          otherLiabilityAmount: undefined,
                                        },
                                      };
                                      onChange(newData);
                                      form.setFieldValue("liabilities", newData.liabilities);
                                    } else {
                                      handleNestedChange("liabilities", "hasOtherLiabilities", val);
                                    }
                                  }}
                                />
                              </div>
                              {data.liabilities?.hasOtherLiabilities && (
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item label="名稱">
                                      <Input
                                        placeholder="請輸入"
                                        value={data.liabilities?.otherLiabilityName}
                                        onChange={(e) => handleNestedChange("liabilities", "otherLiabilityName", e.target.value)}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label="金額">
                                      <AmountInput
                                        style={{ width: "100%" }}
                                        suffix={currencySuffix}
                                        suffixVariant={currencySuffixVariant}
                                        placeholder="請輸入"
                                        value={data.liabilities?.otherLiabilityAmount ?? 0}
                                        onChange={(val) => {
                                          handleNestedChange("liabilities", "otherLiabilityAmount", val ?? 0);
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              )}
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  </CustomCard>

                  {!isZuuFinancialStepMode && (
                    <CustomCard title="二、現行保障 (HK$/港幣)" className={styles.sectionCard}>
                      <div style={{ marginBottom: 24, borderRadius: 6 }}>
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="人壽保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.lifeInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "lifeInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="危疾保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.criticalIllnessInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "criticalIllnessInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="醫療保險類別">
                              <SelectInput
                                options={getOptions("MEDICAL_INSURANCE_TYPE", [])}
                                value={data.protection?.medicalInsuranceType}
                                onChange={(val) => handleNestedChange("protection", "medicalInsuranceType", val ?? 0)}
                                title="醫療保險類別"
                                readOnly={readOnly}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="醫療保險金額">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.medicalInsuranceAmount ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "medicalInsuranceAmount", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="意外保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.accidentInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "accidentInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="其他現有保障">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.otherExistingProtection ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "otherExistingProtection", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="附件資料/備註">
                              <Input placeholder="請輸入" value={data.protection?.attachmentRemarks} onChange={(e) => handleNestedChange("protection", "attachmentRemarks", e.target.value)} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </CustomCard>
                  )}
                </>
              </Form>
            )}
          </>
        )}

        {activeSection === "fna" && !!orderId && (
          <>
            {shouldShowZuuReferenceLatestFna && isZuuFinancialStepMode && zuuReferenceLatestFna ? (
              <CustomCard title="引用最近一份 FNA" className={cx(styles.sectionCard, styles.zuuSectionCard)}>
                <div className={styles.zuuAdvisoryPanel}>
                  <div className={styles.zuuReferenceMeta}>
                    最近 FNA
                    {zuuReferenceLatestFna.orderNo ? `：訂單號 ${zuuReferenceLatestFna.orderNo}` : ""}
                    {zuuReferenceLatestFna.signTime ? `，更新時間 ${zuuReferenceLatestFna.signTime}` : ""}
                  </div>
                  <div className={styles.zuuAdvisoryNote}>只會引用最近一份 FNA 的分析內容，不會覆蓋「產品介紹及選擇」。</div>
                  <div>
                    <CustomButton onClick={zuuReferenceLatestFna.onReference}>引用最近一份 FNA</CustomButton>
                  </div>
                </div>
              </CustomCard>
            ) : null}
            {isZuuFinancialStepMode ? (
              <>
                {premiumCapacityWarnings.length > 0 ? (
                  <CustomCard title="保費繳付能力提示" className={cx(styles.sectionCard, styles.zuuSectionCard)}>
                    <div className={styles.zuuAdvisoryPanel}>
                      {premiumCapacityWarnings.map((warning) => (
                        <div key={warning} className={styles.zuuAdvisoryItem}>
                          {warning}
                        </div>
                      ))}
                      <div className={styles.zuuAdvisoryNote}>此提示只作提醒，不會阻止儲存或進入下一步。</div>
                    </div>
                  </CustomCard>
                ) : null}
                <ZuuFnaForm
                  form={form}
                  readOnly={readOnly}
                  detailDisplay={detailDisplay}
                  className={cx(styles.financialForm, styles.zuuFinancialStepForm, detailDisplay && styles.zuuDetailDisplayForm)}
                  zuuFinancialAnalysisProps={zuuFinancialAnalysisProps}
                  zuuPremiumFinancingProps={zuuPremiumFinancingProps}
                  zuuProductSelectionProps={zuuProductSelectionProps}
                  zuuOnlyProvideOneProductProps={zuuOnlyProvideOneProductProps}
                  zuuSuggestionsProps={zuuSuggestionsProps}
                />
              </>
            ) : (
              <LegacyFnaForm
                form={form}
                readOnly={readOnly}
                className={styles.financialForm}
                data={data}
                onChange={onChange}
                companyList={companyList}
                productSectionRef={productSectionRef}
                productValidation={productValidation}
                onOpenSelector={onOpenSelector}
                onMessageError={message.error}
                handleChange={handleChange}
                policyholderBirthDate={orderData?.policyholder?.basic?.birthDate}
                intendedRetirementAge={orderData?.policyholder?.work?.intendedRetirementAge}
                onAddPremiumFinancing={handleAddPremiumFinancing}
                onRemovePremiumFinancing={handleRemovePremiumFinancing}
                onPremiumFinancingChange={handlePremiumFinancingChange}
                monthlyDisposableIncome={a_monthly}
                yearlyDisposableIncome={a_yearly}
                monthlyDisposableIncomePremiumLimit={a1_monthly}
                yearlyDisposableIncomePremiumLimit={a1_yearly}
                liquidAssetsAmount={b_assets}
                liquidAssetsPremiumLimit={b1_amount}
                formatCurrency={formatCurrency}
                formatInteger={formatInteger}
              />
            )}
          </>
        )}
        {/* </>
        )} */}
      </div>
    );
  },
);
